import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { ManaSymbol } from "@/components/common/ManaSymbol";

export const EMPTY_FILTERS = { starts: "", supertype: "", type: "", subtype: "", colors: [], colorless: false, color_mode: "include", color_by: "color",
  rarity: "", artist: "", mv_min: "", mv_max: "", set: "", text: "", legal_only: false };
const SUPERTYPES = ["Legendary", "Basic", "Snow", "World", "Kindred"];
const TYPES = ["Creature", "Instant", "Sorcery", "Artifact", "Enchantment", "Land", "Planeswalker", "Battle", "Plane", "Phenomenon", "Scheme", "Vanguard"];
const RARITIES = ["common", "uncommon", "rare", "mythic", "special"];
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const MODES = { include: "Including these", exact: "Exactly these", atmost: "At most these" };

const quote = (s) => (/\s/.test(s) ? `"${s.replace(/"/g, "")}"` : s);

/** Scryfall query string from the name box + filters. */
export function toScryfall(q, f, formatKey) {
  const p = [q.trim()];
  const key = f.color_by === "identity" ? "id" : "c";
  if (f.starts) p.push(`name:/^${f.starts.toLowerCase()}/`);
  [f.supertype, f.type, f.subtype].filter(Boolean).forEach((t) => p.push(`t:${quote(t.trim())}`));
  if (f.colorless) p.push(`${key}=c`);
  else if (f.colors.length) p.push(`${key}${{ include: ">=", exact: "=", atmost: "<=" }[f.color_mode]}${f.colors.join("").toLowerCase()}`);
  if (f.rarity) p.push(`r:${f.rarity}`);
  if (f.artist.trim()) p.push(`a:${quote(f.artist.trim())}`);
  if (f.mv_min !== "") p.push(`mv>=${f.mv_min}`);
  if (f.mv_max !== "") p.push(`mv<=${f.mv_max}`);
  if (f.set.trim()) p.push(`s:${f.set.trim()}`);
  if (f.text.trim()) p.push(`o:${quote(f.text.trim())}`);
  if (f.legal_only && formatKey) p.push(`f:${formatKey}`);
  return p.filter(Boolean).join(" ");
}

/** Query params for the custom card library. */
export const toCustomParams = (q, f) => ({
  q: q.trim() || undefined, starts: f.starts || undefined, supertype: f.supertype || undefined, type: f.type || undefined, subtype: f.subtype.trim() || undefined,
  colors: f.colors.join("") || undefined, color_mode: f.colors.length ? (f.color_mode === "include" ? "all" : f.color_mode) : undefined, colorless: f.colorless || undefined,
  rarity: f.rarity || undefined, artist: f.artist.trim() || undefined, text: f.text.trim() || undefined,
  mv_min: f.mv_min === "" ? undefined : f.mv_min, mv_max: f.mv_max === "" ? undefined : f.mv_max,
});

export const activeCount = (f) => Object.entries(f).filter(([k, v]) => !["color_mode", "color_by"].includes(k) && (Array.isArray(v) ? v.length : v)).length;

function Pick({ label, value, options, onChange, testid }) {
  return (
    <label className="text-[10px] uppercase tracking-wider text-slate-500">{label}
      <select className="field mt-0.5 py-1 text-xs normal-case tracking-normal" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">Any</option>
        {options.map((o) => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
      </select>
    </label>
  );
}

function Text({ label, value, onChange, testid, ...rest }) {
  return (
    <label className="text-[10px] uppercase tracking-wider text-slate-500">{label}
      <input className="field mt-0.5 py-1 text-xs normal-case tracking-normal" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest} />
    </label>
  );
}

function ColorPicker({ f, set, official }) {
  const toggle = (c) => set({ colorless: false, colors: f.colors.includes(c) ? f.colors.filter((x) => x !== c) : [...f.colors, c] });
  return (
    <div className="col-span-2 space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {["W", "U", "B", "R", "G"].map((c) => (
          <button key={c} type="button" onClick={() => toggle(c)} aria-pressed={f.colors.includes(c)} data-testid={`deck-filter-color-${c}`}
            className={`rounded-full p-0.5 transition-opacity ${f.colors.includes(c) ? "ring-2 ring-amber-400" : "opacity-40 hover:opacity-80"}`}><ManaSymbol sym={c} size={22} /></button>
        ))}
        <button type="button" onClick={() => set({ colorless: !f.colorless, colors: [] })} aria-pressed={f.colorless} data-testid="deck-filter-colorless"
          className={`rounded-full p-0.5 transition-opacity ${f.colorless ? "ring-2 ring-amber-400" : "opacity-40 hover:opacity-80"}`}><ManaSymbol sym="C" size={22} /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select className="field py-1 text-xs" value={f.color_mode} onChange={(e) => set({ color_mode: e.target.value })} aria-label="Colour match" data-testid="deck-filter-color-mode">
          {Object.entries(MODES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {official ? (
          <select className="field py-1 text-xs" value={f.color_by} onChange={(e) => set({ color_by: e.target.value })} aria-label="Colour or identity" data-testid="deck-filter-color-by">
            <option value="color">Card colour</option><option value="identity">Colour identity</option>
          </select>
        ) : <span className="self-center text-[10px] text-slate-500">Matches colour identity</span>}
      </div>
    </div>
  );
}

export function SearchFilters({ f, onChange, official, formatKey, formatName, open, onToggle }) {
  const set = (patch) => onChange({ ...f, ...patch });
  const n = activeCount(f);
  return (
    <div className="mt-2 rounded-md border border-[#2A3447]" data-testid="deck-search-filters">
      <button type="button" onClick={onToggle} className="flex w-full items-center justify-between px-3 py-2 text-xs text-slate-300 hover:text-aether-200" aria-expanded={open} data-testid="deck-search-filters-toggle">
        <span className="flex items-center gap-1.5"><SlidersHorizontal className="h-3.5 w-3.5" />Filters{n ? <span className="rounded bg-amber-600/30 px-1.5 text-amber-100" data-testid="deck-search-filters-count">{n}</span> : null}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="grid grid-cols-2 gap-2 border-t border-[#2A3447] p-3">
          <Pick label="Starts with" value={f.starts} options={LETTERS} onChange={(v) => set({ starts: v })} testid="deck-filter-starts" />
          <Pick label="Supertype" value={f.supertype} options={SUPERTYPES} onChange={(v) => set({ supertype: v })} testid="deck-filter-supertype" />
          <Pick label="Type" value={f.type} options={TYPES} onChange={(v) => set({ type: v })} testid="deck-filter-type" />
          <Text label="Subtype" value={f.subtype} onChange={(v) => set({ subtype: v })} placeholder="e.g. Elf, Equipment" maxLength={40} testid="deck-filter-subtype" />
          <ColorPicker f={f} set={set} official={official} />
          <Pick label="Rarity" value={f.rarity} options={RARITIES} onChange={(v) => set({ rarity: v })} testid="deck-filter-rarity" />
          <Text label="Artist" value={f.artist} onChange={(v) => set({ artist: v })} placeholder="e.g. Rebecca Guay" maxLength={80} testid="deck-filter-artist" />
          <Text label="Mana value from" type="number" min={0} max={20} value={f.mv_min} onChange={(v) => set({ mv_min: v })} testid="deck-filter-mv-min" />
          <Text label="Mana value to" type="number" min={0} max={20} value={f.mv_max} onChange={(v) => set({ mv_max: v })} testid="deck-filter-mv-max" />
          <Text label="Rules text" value={f.text} onChange={(v) => set({ text: v })} placeholder="e.g. draw a card" maxLength={120} testid="deck-filter-text" />
          {official && <Text label="Set code" value={f.set} onChange={(v) => set({ set: v })} placeholder="e.g. dmu" maxLength={8} testid="deck-filter-set" />}
          {official && formatKey && (
            <label className="col-span-2 flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={f.legal_only} onChange={(e) => set({ legal_only: e.target.checked })} data-testid="deck-filter-legal-only" />Only cards legal in {formatName}
            </label>
          )}
          <button type="button" className="btn-ghost col-span-2 text-xs" onClick={() => onChange({ ...EMPTY_FILTERS })} data-testid="deck-search-filters-clear">Clear filters</button>
        </div>
      )}
    </div>
  );
}
